// Session token storage.
//
// Tokens live in `sessionStorage` (NOT localStorage) so they auto-clear on tab
// close and the XSS exposure window is shorter. The Cognito refresh token has
// a 30-day TTL but stays in sessionStorage too — losing it on tab close is the
// intended posture for an admin tool.
//
// Anything that is one-shot or short-lived (MFA challenge session, password
// reset token) MUST stay in memory and NEVER touch storage — see auth handoff
// §5 "Token handling".

const KEY_PREFIX = 'gccoach.admin.';
const ACCESS_TOKEN_KEY = `${KEY_PREFIX}accessToken`;
const ID_TOKEN_KEY = `${KEY_PREFIX}idToken`;
const REFRESH_TOKEN_KEY = `${KEY_PREFIX}refreshToken`;
const EXPIRES_AT_KEY = `${KEY_PREFIX}expiresAt`;

export interface StoredTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  /** Unix epoch milliseconds at which the access/id tokens expire. */
  expiresAt: number;
}

export function loadTokens(): StoredTokens | null {
  const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  const idToken = sessionStorage.getItem(ID_TOKEN_KEY);
  const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
  const expiresAtRaw = sessionStorage.getItem(EXPIRES_AT_KEY);
  if (!accessToken || !idToken || !refreshToken || !expiresAtRaw) return null;
  const expiresAt = Number(expiresAtRaw);
  if (!Number.isFinite(expiresAt)) return null;
  return { accessToken, idToken, refreshToken, expiresAt };
}

export function saveTokens(tokens: StoredTokens): void {
  sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  sessionStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
  sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  sessionStorage.setItem(EXPIRES_AT_KEY, String(tokens.expiresAt));
}

export function clearTokens(): void {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(ID_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(EXPIRES_AT_KEY);
}
